import { Fragment, type ReactNode } from 'react';
import { Separator } from '@voll-intelligence/ui-kit/ui/separator';
import { Skeleton } from '@voll-intelligence/ui-kit/ui/skeleton';
import { ResultsFiltersPanel } from '../../shared/results_page/results_filters_panel.js';
import { useSkeletonMotion } from '../../shared/skeleton_motion.js';
import type { HotelResultsFiltersCopy } from './hotel_results_filters.js';

/** As linhas de cada seção da coluna real — política, tarifa, diária, nota,
 *  distância: a silhueta tem a altura da coluna que vem, e ela não salta. */
const SECTIONS = [
  { name: 'policy', rows: 1 },
  { name: 'fare', rows: 2 },
  { name: 'nightly', rows: 2 },
  { name: 'rating', rows: 4 },
  { name: 'distance', rows: 3 },
] as const;
/** Os chips da fila do celular: Filtros, ordenador e os cortes rápidos. */
const CHIP_WIDTHS = ['w-24', 'w-32', 'w-40', 'w-28'] as const;

/** O corpo da coluna enquanto a busca inicial corre (#5507): sem ofertas, os
 *  eixos condicionados ao dado somem e só a Política sobrava — travada e
 *  sozinha, lia como carregada. Decorativo: quem anuncia é o status da meta. */
function HotelFiltersSkeletonBody(): ReactNode {
  const still = useSkeletonMotion();
  return (
    <div aria-hidden="true" className="grid gap-5">
      {SECTIONS.map(({ name, rows }, section) => (
        <Fragment key={name}>
          {section > 0 && <Separator />}
          <div className="grid gap-3">
            <Skeleton className={`h-4 w-24 ${still}`} />
            {Array.from({ length: rows }, (_, row) => (
              <Skeleton key={row} className={`h-5 w-full ${still}`} />
            ))}
          </div>
        </Fragment>
      ))}
    </div>
  );
}

/**
 * A coluna de cortes do hotel com a busca inicial em voo: a MESMA casca do
 * painel (heading `Filtros`, `Redefinir` desabilitado) com o esqueleto no
 * lugar das seções. Com `onClose` é a folha do celular, com o X dela; sem ele,
 * o corpo da coluna da linha (o heading é do `ResultsLayout`).
 */
export function HotelResultsFiltersSkeleton({
  copy,
  onClose,
}: {
  readonly copy: HotelResultsFiltersCopy;
  readonly onClose?: () => void;
}): ReactNode {
  const shell = onClose
    ? ({ dismiss: 'close', onCollapse: onClose } as const)
    : ({ header: false } as const);
  return (
    <ResultsFiltersPanel
      {...shell}
      copy={copy}
      active={false}
      locked
      frame={false}
      onReset={() => undefined}
    >
      <HotelFiltersSkeletonBody />
    </ResultsFiltersPanel>
  );
}

/** A fila de chips do celular enquanto a busca inicial corre: a silhueta da
 *  fila, sem botão — um chip de Política sozinho lia como a fila pronta. */
export function HotelFilterChipsSkeleton(): ReactNode {
  const still = useSkeletonMotion();
  return (
    <div aria-hidden="true" className="flex gap-2 overflow-hidden px-4 py-2">
      {CHIP_WIDTHS.map((width) => (
        <Skeleton key={width} className={`h-8 shrink-0 rounded-full ${width} ${still}`} />
      ))}
    </div>
  );
}
